import { useState } from "react";
import { Stack, Typography, TextField, Button, IconButton, InputAdornment, Tooltip, Link } from "@mui/material";
import { Database, theme } from "@zesty-io/material";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckIcon from "@mui/icons-material/Check";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ContentModelField } from "../../../../../../../shell/services/types";
import { FieldIcon } from "../../../../../../schema/src/app/components/Field/FieldIcon";
import { getTypeText, FieldType } from "../../../../../../schema/src/app/components/configs";
import { formatLocalized } from "shell/i18n/dates";
type FieldTooltipBodyProps = {
  data: Partial<ContentModelField>;
};
export const FieldTooltipBody = ({
  data
}: FieldTooltipBodyProps) => {
  const {
    t
  } = useTranslation(["content", "schema"]);
  const TYPE_TEXT = getTypeText(t);
  const history = useHistory();
  return <>
      <Stack p={1.25} direction="row" alignItems="center" justifyContent="space-between" borderRadius="inherit" sx={{
      backgroundColor: "grey.50"
    }} data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
        <Stack direction="row" alignItems="center" gap={1.5} data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
          <FieldIcon type={data?.datatype} data-sentry-element="FieldIcon" data-sentry-source-file="FieldTooltipBody.tsx" />
          <Stack data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
            <Typography variant="body2" fontWeight={600} noWrap maxWidth={200} data-sentry-element="Typography" data-sentry-source-file="FieldTooltipBody.tsx">
              {data?.label} {data?.required && "*"}
            </Typography>
            <Typography variant="body3" fontWeight={600} color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="FieldTooltipBody.tsx">
              {t("content.fieldTypeLabel", {
              type: TYPE_TEXT[data?.datatype as FieldType]
            })}
            </Typography>
          </Stack>
        </Stack>
        <Button variant="contained" color="inherit" size="small" startIcon={<Database color="action" />} onClick={() => history.push(`/schema/${data?.contentModelZUID}/fields/${data?.ZUID}`)} sx={{
        flexShrink: 0
      }} data-sentry-element="Button" data-sentry-source-file="FieldTooltipBody.tsx">
          {t("content.editFieldButton")}
        </Button>
      </Stack>
      <Stack gap={2.5} p={1.5} data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
        <CopyField value={data?.name} title={t("content.apiCodeIdLabel")} tooltip={t("content.apiCodeIdTooltip")} data-sentry-element="CopyField" data-sentry-source-file="FieldTooltipBody.tsx" />
        <CopyField value={data?.ZUID} title={t("content.fieldZuidLabel")} tooltip={t("content.fieldZuidTooltip")} data-sentry-element="CopyField" data-sentry-source-file="FieldTooltipBody.tsx" />
        <Stack direction="row" justifyContent="space-between" data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
          <Typography variant="body3" fontWeight={600} color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="FieldTooltipBody.tsx">
            {t("content.fieldAddedOnLabel")}{" "}
            {data?.createdAt ? formatLocalized(new Date(data.createdAt), "MMM d, yyyy") : ""}
          </Typography>
          <Link component={RouterLink} to={`/schema/${data?.contentModelZUID}/activity-log`} color="text.secondary" underline="always" variant="body3" fontWeight={600} sx={{
          textDecorationColor: theme.palette.text.secondary,
          "&:hover": {
            textDecorationColor: theme.palette.text.secondary
          }
        }} data-sentry-element="Link" data-sentry-source-file="FieldTooltipBody.tsx">
            {t("content.viewModelActivityLog")}
          </Link>
        </Stack>
      </Stack>
    </>;
};
type CopyFieldProps = {
  value: string;
  title: string;
  tooltip?: string;
};
const CopyField = ({
  value,
  title,
  tooltip
}: CopyFieldProps) => {
  const [isCopied, setIsCopied] = useState(false);
  return <Stack gap={0.5} data-sentry-element="Stack" data-sentry-component="CopyField" data-sentry-source-file="FieldTooltipBody.tsx">
      <Stack direction="row" gap={1} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="FieldTooltipBody.tsx">
        <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="FieldTooltipBody.tsx">{title}</Typography>
        <Tooltip title={tooltip} placement="top" data-sentry-element="Tooltip" data-sentry-source-file="FieldTooltipBody.tsx">
          <InfoRoundedIcon color="action" sx={{
          fontSize: 12
        }} data-sentry-element="InfoRoundedIcon" data-sentry-source-file="FieldTooltipBody.tsx" />
        </Tooltip>
      </Stack>
      <TextField disabled value={value} inputProps={{
      sx: {
        ":read-only": {
          textFillColor: theme.palette.text.primary
        }
      }
    }} InputProps={{
      endAdornment: <InputAdornment position="end">
              <IconButton size="small" onClick={() => {
          navigator?.clipboard?.writeText(value).then(() => {
            setIsCopied(true);
            setTimeout(() => {
              setIsCopied(false);
            }, 3000);
          }).catch(err => {
            console.error(err);
          });
        }}>
                {isCopied ? <CheckIcon color="action" /> : <ContentCopyRoundedIcon color="action" />}
              </IconButton>
            </InputAdornment>
    }} data-sentry-element="TextField" data-sentry-source-file="FieldTooltipBody.tsx" />
    </Stack>;
};